import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, IconButton, Typography, TextField, Avatar, Grow, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const AIChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { text: "Hi! I'm your IIT Patna Campus Assistant. How can I help you today?", isUser: false }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSend = () => {
        if (!input.trim()) return;

        const userMsg = input.trim();
        setMessages(prev => [...prev, { text: userMsg, isUser: true }]);
        setInput('');
        setIsTyping(true);

        setTimeout(() => {
            let aiResponse = "I'm not entirely sure about that. Try asking me about your classes, notices, or a computer science concept!";
            const lowerInput = userMsg.toLowerCase();
            if (lowerInput.includes('class') || lowerInput.includes('tomorrow')) {
                aiResponse = "Tomorrow, you have 'Operating Systems (CS302)' at 9:00 AM, followed by 'Computer Networks (CS303)' at 11:00 AM.";
            } else if (lowerInput.includes('notice') || lowerInput.includes('dean')) {
                aiResponse = "The latest notice from the Dean states that the mid-semester exams will begin on September 15th. Make sure to submit all pending assignments before then!";
            } else if (lowerInput.includes('paging') || lowerInput.includes('operating system')) {
                aiResponse = "Paging is a memory management scheme in OS that eliminates the need for contiguous allocation of physical memory. It divides physical memory into fixed-size blocks called 'frames' and logical memory into blocks of the same size called 'pages'.";
            } else if (lowerInput.includes('hello') || lowerInput.includes('hi')) {
                aiResponse = "Hello there! Ready to crush your assignments today?";
            }

            setMessages(prev => [...prev, { text: aiResponse, isUser: false }]);
            setIsTyping(false);
        }, 1500);
    };

    return (
        <Box sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(102, 126, 234, 0.7)' },
                '70%': { boxShadow: '0 0 0 15px rgba(102, 126, 234, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(102, 126, 234, 0)' }
            }
        }}>
            {/* Chat Window */}
            <Grow in={isOpen}>
                <Paper 
                    elevation={6}
                    sx={{
                        width: 350,
                        height: 500,
                        borderRadius: '16px',
                        display: isOpen ? 'flex' : 'none',
                        flexDirection: 'column',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        mb: 3,
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}
                >
                    <Box sx={{ 
                        p: 2, 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src="/assets/ai-avatar.png" sx={{ bgcolor: 'white', width: 44, height: 44, border: '2px solid rgba(255,255,255,0.5)' }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: '700', lineHeight: 1.2 }}>Campus Buddy</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>AI Assistant</Typography>
                            </Box>
                        </Box>
                        <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{
                        flex: 1,
                        p: 2,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        background: '#f8fafc'
                    }}>
                        {messages.map((msg, idx) => (
                            <Box key={idx} sx={{
                                maxWidth: '80%',
                                p: 1.5,
                                borderRadius: '16px',
                                fontSize: '0.9rem',
                                lineHeight: 1.4,
                                wordWrap: 'break-word',
                                alignSelf: msg.isUser ? 'flex-end' : 'flex-start',
                                background: msg.isUser ? '#667eea' : 'white',
                                color: msg.isUser ? 'white' : '#334155',
                                borderBottomRightRadius: msg.isUser ? '4px' : '16px',
                                borderBottomLeftRadius: msg.isUser ? '16px' : '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                            }}>
                                {msg.text}
                            </Box>
                        ))}
                        {isTyping && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, mt: 1, color: '#764ba2' }}>
                                <CircularProgress size={12} sx={{ color: '#764ba2' }} />
                                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Buddy is typing...</Typography>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    <Box sx={{
                        p: 2,
                        background: 'white',
                        borderTop: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                    }}>
                        <TextField 
                            fullWidth 
                            variant="outlined" 
                            placeholder="Ask me anything..." 
                            size="small"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
                        />
                        <IconButton color="primary" onClick={handleSend} sx={{ background: '#f1f5f9', '&:hover': { background: '#e2e8f0' } }}>
                            <SendIcon fontSize="small" sx={{ color: '#667eea' }} />
                        </IconButton>
                    </Box>
                </Paper>
            </Grow>

            {/* Floating FAB */}
            {!isOpen && (
                <Box 
                    onClick={() => setIsOpen(true)}
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 10px 25px rgba(118, 75, 162, 0.4)',
                        transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        animation: 'pulse 2s infinite',
                        border: '3px solid #667eea',
                        '&:hover': { transform: 'scale(1.1)' }
                    }}
                >
                    <Box 
                        component="img"
                        src="/assets/ai-avatar.png"
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            objectFit: 'cover'
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default AIChatWidget;
